<template>
  <div v-if="visible" class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-box" :class="{ 'modal-box--dos-cols': totalEur > 0 && totalUsd > 0 }">
      <div class="q-modal-header">
        <span class="q-modal-title">Ingresos reales</span>
        <span v-if="dateFrom && dateTo" class="modal-rango">{{ dateFrom }} → {{ dateTo }}</span>
        <button class="modal-close" @click="$emit('close')">✕</button>
      </div>

      <div v-if="loading" class="modal-loading">Cargando...</div>
      <div v-else-if="!ventas.length" class="modal-empty">Sin ingresos en el periodo seleccionado.</div>

      <div v-else class="modal-body">

        <!-- Pestañas EUR/USD — solo visibles en móvil cuando hay ambas monedas -->
        <div v-if="totalEur > 0 && totalUsd > 0" class="tabs-moneda">
          <button :class="['tab-mon', tabMoneda === 'eur' && 'tab-mon--active tab-mon--eur']" @click="tabMoneda = 'eur'">
            € EUR &nbsp;<span class="tab-total">{{ fmt(totalEur) }}</span>
          </button>
          <button :class="['tab-mon', tabMoneda === 'usd' && 'tab-mon--active tab-mon--usd']" @click="tabMoneda = 'usd'">
            $ USD &nbsp;<span class="tab-total">{{ fmtUsd(totalUsd) }}</span>
          </button>
        </div>

        <div class="columnas" :class="['columnas--tab-' + tabMoneda, { 'columnas--dos': totalEur > 0 && totalUsd > 0 }]">

          <!-- Columna EUR -->
          <div v-if="totalEur > 0" class="col-moneda col-eur">
            <div class="col-moneda-header">
              <span class="moneda-badge moneda-badge--eur">€ EUR</span>
              <span class="moneda-total">{{ fmt(totalEur) }}</span>
            </div>
            <div class="resumen-tipos">
              <div v-for="t in resumenTipos" :key="t.tipo" class="tipo-row">
                <span :class="['tipo-badge', 'tipo-' + t.tipo]">{{ tipoLabel(t.tipo) }}</span>
                <span class="tipo-count">{{ t.count }}</span>
                <span class="tipo-total">{{ fmt(t.total) }}</span>
              </div>
            </div>
            <div class="section-title">Por producto</div>
            <div class="productos-list">
              <div v-for="g in grupos" :key="g.producto_id" class="producto-card">
                <div class="producto-header">
                  <span class="producto-nombre">{{ g.producto }}</span>
                  <span class="producto-total">{{ fmt(g.total) }}</span>
                </div>
                <div class="producto-tipos">
                  <div v-for="t in g.tipos" :key="t.tipo" class="producto-tipo-row">
                    <span :class="['tipo-badge', 'tipo-badge--sm', 'tipo-' + t.tipo]">{{ tipoLabel(t.tipo) }}</span>
                    <span class="pt-count">{{ t.count }}</span>
                    <span class="pt-total">{{ fmt(t.total) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Columna USD -->
          <div v-if="totalUsd > 0" class="col-moneda col-usd">
            <div class="col-moneda-header">
              <span class="moneda-badge moneda-badge--usd">$ USD</span>
              <span class="moneda-total moneda-total--usd">{{ fmtUsd(totalUsd) }}</span>
            </div>
            <div class="resumen-tipos">
              <div v-for="t in resumenTiposUsd" :key="t.tipo" class="tipo-row">
                <span :class="['tipo-badge', 'tipo-' + t.tipo]">{{ tipoLabel(t.tipo) }}</span>
                <span class="tipo-count">{{ t.count }}</span>
                <span class="tipo-total tipo-total--usd">{{ fmtUsd(t.total) }}</span>
              </div>
            </div>
            <div class="section-title section-title--usd">Por producto</div>
            <div class="productos-list">
              <div v-for="g in gruposUsd" :key="g.producto_id" class="producto-card">
                <div class="producto-header">
                  <span class="producto-nombre">{{ g.producto }}</span>
                  <span class="producto-total producto-total--usd">{{ fmtUsd(g.total) }}</span>
                </div>
                <div class="producto-tipos">
                  <div v-for="t in g.tipos" :key="t.tipo" class="producto-tipo-row">
                    <span :class="['tipo-badge', 'tipo-badge--sm', 'tipo-' + t.tipo]">{{ tipoLabel(t.tipo) }}</span>
                    <span class="pt-count">{{ t.count }}</span>
                    <span class="pt-total">{{ fmtUsd(t.total) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- Total general -->
        <div class="total-bar">
          <span class="total-label">Total ingresos</span>
          <span v-if="totalEur > 0" class="total-eur">{{ fmt(totalEur) }}</span>
          <span v-if="totalUsd > 0" class="total-usd">{{ fmtUsd(totalUsd) }}</span>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import panelesService from "@/services/paneles.service";

export default {
  name: "ModalResumenIngresos",
  props: {
    visible:   { type: Boolean, default: false },
    dateFrom:  { type: String,  default: null },
    dateTo:    { type: String,  default: null },
    tipsterId: { type: [Number, String], default: null },
    canAdmin:  { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return { loading: false, ventas: [], tabMoneda: 'eur' };
  },
  computed: {
    ventasEur() { return this.ventas.filter(v => v.moneda !== 'USD'); },
    totalEur()  { return this.ventasEur.reduce((s, v) => s + parseFloat(v.importe || 0), 0); },
    totalUsd()  { return this.ventas.filter(v => v.moneda === 'USD').reduce((s, v) => s + parseFloat(v.importe || 0), 0); },

    resumenTipos()    { return this._resumenTiposDe(this.ventasEur); },
    resumenTiposUsd() { return this._resumenTiposDe(this.ventas.filter(v => v.moneda === 'USD')); },
    grupos()    { return this._gruposDe(this.ventasEur); },
    gruposUsd() { return this._gruposDe(this.ventas.filter(v => v.moneda === 'USD')); },
  },
  watch: {
    visible(val) { if (val) this.cargar(); },
  },
  methods: {
    async cargar() {
      this.loading = true;
      this.ventas = [];
      this.tabMoneda = 'eur';
      try {
        const payload = {};
        if (this.dateFrom) payload.date_from = this.dateFrom;
        if (this.dateTo)   payload.date_to   = this.dateTo;
        let res;
        if (this.canAdmin && this.tipsterId) {
          res = await panelesService.ventasDetalleAdmin(this.tipsterId, payload);
        } else {
          res = await panelesService.ventasDetalle(payload);
        }
        this.ventas = res?.data ?? [];
      } catch {
        this.ventas = [];
      } finally {
        this.loading = false;
      }
    },
    _resumenTiposDe(lista) {
      const map = {};
      for (const v of lista) {
        if (!map[v.tipo]) map[v.tipo] = { tipo: v.tipo, count: 0, total: 0 };
        map[v.tipo].count++;
        map[v.tipo].total += parseFloat(v.importe || 0);
      }
      return Object.values(map).sort((a, b) => b.total - a.total);
    },
    _gruposDe(lista) {
      const map = {};
      for (const v of lista) {
        const k = v.producto_id;
        if (!map[k]) map[k] = { producto_id: k, producto: v.producto, total: 0, tipos: {} };
        map[k].total += parseFloat(v.importe || 0);
        if (!map[k].tipos[v.tipo]) map[k].tipos[v.tipo] = { tipo: v.tipo, count: 0, total: 0 };
        map[k].tipos[v.tipo].count++;
        map[k].tipos[v.tipo].total += parseFloat(v.importe || 0);
      }
      return Object.values(map)
        .sort((a, b) => b.total - a.total)
        .map(g => ({ ...g, tipos: Object.values(g.tipos).sort((a, b) => b.total - a.total) }));
    },
    fmt(n)    { return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" }); },
    fmtUsd(n) { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" }); },
    tipoLabel(t) {
      return { compra_directa: "Compra directa", suscripcion_nueva: "Suscripción nueva", renovacion: "Renovación" }[t] ?? t;
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  z-index: 1050; display: flex; align-items: center;
  justify-content: center; padding: 16px;
}
.modal-box {
  background: var(--q-card); border: 1px solid var(--q-border);
  border-radius: 16px; width: 100%; max-width: 520px;
  max-height: 85vh; display: flex; flex-direction: column; overflow: hidden;
  transition: max-width .2s;
}
.modal-box--dos-cols { max-width: 760px; }

.q-modal-header {
  display: flex; align-items: center; gap: 10px;
  padding: 16px 20px; border-bottom: 1px solid var(--q-border); flex-shrink: 0;
}
.q-modal-title { margin: 0; font-size: 15px; font-weight: 700; flex: 1; color: #d4af37; }
.modal-rango  { font-size: 12px; opacity: .6; white-space: nowrap; }
.modal-close  { background: none; border: none; cursor: pointer; font-size: 16px; opacity: .5; padding: 4px 6px; color: var(--q-text); }
.modal-close:hover { opacity: 1; }
.modal-loading, .modal-empty { padding: 40px 20px; text-align: center; opacity: .6; font-size: 14px; }
.modal-body { overflow-y: auto; flex: 1; display: flex; flex-direction: column; }

/* Layout dos columnas */
.columnas { display: flex; flex-direction: column; flex: 1; }
.columnas--dos { flex-direction: row; }

.col-moneda {
  display: flex; flex-direction: column;
  flex: 1; min-width: 0;
}
.columnas--dos .col-eur {
  border-right: 1px solid var(--q-border);
}

/* Cabecera de columna */
.col-moneda-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; background: rgba(128,128,128,.04);
  border-bottom: 1px solid var(--q-border);
  flex-shrink: 0;
}
.moneda-badge {
  font-size: 12px; font-weight: 700; letter-spacing: .05em;
  padding: 3px 10px; border-radius: 20px;
}
.moneda-badge--eur { background: rgba(212,175,55,.15); color: #d4af37; }
.moneda-badge--usd { background: rgba(38,198,218,.15); color: #26c6da; }
.moneda-total { font-size: 16px; font-weight: 800; color: #d4af37; font-variant-numeric: tabular-nums; }
.moneda-total--usd { color: #26c6da; }

/* Resumen tipos */
.resumen-tipos {
  display: flex; flex-direction: column; gap: 5px;
  padding: 12px 16px; border-bottom: 1px solid var(--q-border);
  flex-shrink: 0;
}
.tipo-row { display: flex; align-items: center; gap: 8px; }
.tipo-count { font-size: 12px; opacity: .55; flex: 1; }
.tipo-total { font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--q-gold, #d4af37); white-space: nowrap; }
.tipo-total--usd { color: #26c6da; }

.section-title {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .06em; color: #b8962e; padding: 10px 16px 6px;
  flex-shrink: 0;
}
.section-title--usd { color: #00acc1; }

/* Lista productos */
.productos-list { display: flex; flex-direction: column; gap: 6px; padding: 0 16px 12px; overflow-y: auto; }
.producto-card { border: 1px solid var(--q-border); border-radius: 8px; overflow: hidden; }
.producto-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; background: rgba(128,128,128,.05); gap: 8px;
}
.producto-nombre { font-size: 12px; font-weight: 600; flex: 1; line-height: 1.3; }
.producto-total  { font-size: 13px; font-weight: 700; color: #d4af37; font-variant-numeric: tabular-nums; white-space: nowrap; }
.producto-total--usd { color: #26c6da; }
.producto-tipos  { display: flex; flex-direction: column; }
.producto-tipo-row {
  display: flex; align-items: center; gap: 8px;
  padding: 5px 12px; border-top: 1px solid var(--q-border);
}
.pt-count { font-size: 12px; font-weight: 600; min-width: 20px; text-align: right; opacity: .7; }
.pt-total { font-size: 12px; font-variant-numeric: tabular-nums; margin-left: auto; opacity: .75; }

/* Total bar */
.total-bar {
  display: flex; align-items: center; gap: 12px; justify-content: flex-end;
  padding: 12px 20px; border-top: 1px solid var(--q-border); flex-shrink: 0;
}
.total-label { font-size: 13px; font-weight: 600; flex: 1; opacity: .7; }
.total-eur { font-size: 17px; font-weight: 700; color: #d4af37; font-variant-numeric: tabular-nums; }
.total-usd { font-size: 17px; font-weight: 700; color: #26c6da; font-variant-numeric: tabular-nums; }

/* Badges */
.tipo-badge {
  display: inline-block; padding: 2px 8px; border-radius: 20px;
  font-size: 10px; font-weight: 600; white-space: nowrap; flex-shrink: 0;
}
.tipo-badge--sm { padding: 1px 6px; font-size: 9px; }
.tipo-compra_directa    { background: rgba(66,165,245,.15); color: #42a5f5; }
.tipo-suscripcion_nueva { background: rgba(102,187,106,.15); color: #66bb6a; }
.tipo-renovacion        { background: rgba(212,175,55,.15); color: var(--q-gold, #d4af37); }

/* Pestañas EUR/USD — ocultas en escritorio */
.tabs-moneda { display: none; }

/* En móvil: pestañas activas, columnas controladas por tab */
@media (max-width: 600px) {
  .modal-box--dos-cols { max-width: 100%; }

  .tabs-moneda {
    display: flex;
    border-bottom: 1px solid var(--q-border);
    flex-shrink: 0;
  }
  .tab-mon {
    flex: 1;
    padding: 10px 8px;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    font-size: 13px;
    font-weight: 600;
    color: var(--q-muted, #8a96a3);
    cursor: pointer;
    transition: color .15s, border-color .15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .tab-mon--active { color: var(--q-text, #e7edf5); }
  .tab-mon--eur.tab-mon--active { border-bottom-color: #d4af37; color: #d4af37; }
  .tab-mon--usd.tab-mon--active { border-bottom-color: #26c6da; color: #26c6da; }
  .tab-total { font-size: 11px; opacity: .8; font-variant-numeric: tabular-nums; }

  /* En móvil: oculta la columna inactiva según la pestaña */
  .columnas--dos { flex-direction: column; }
  .columnas--tab-eur .col-usd { display: none; }
  .columnas--tab-usd .col-eur { display: none; }
  .columnas--dos .col-eur { border-right: none; }
}
</style>
